/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import React from 'react';
import { redirect, type RouteObject } from 'react-router-dom';

import { onBoardingRedirection } from '@fai/friendli-suite/features/routes/loaders/client-team';
import AuthLayout from '@fai/friendli-suite/views/layout/auth/auth-layout';
import {
  docsHeaderLinkProps,
  loginHeaderLinkProps,
  pricingHeaderLinkProps,
  websiteHeaderLinkProps,
} from '@fai/friendli-suite/views/layout/auth/links';
import { pipeLoaders } from '@fai/third-party/react-router-dom';
import { redirectIfSessionExpired } from '@fai/third-party/supertokens';

const AuthLoginPage = React.lazy(() => import('@fai/friendli-suite/pages/auth/login'));
const AuthCliLoginPage = React.lazy(() => import('@fai/friendli-suite/pages/auth/login/cli-login'));
const AuthSignupSelfRegisterPage = React.lazy(
  () => import('@fai/friendli-suite/pages/auth/signup/self-register')
);
const SignupCheckInboxPage = React.lazy(
  () => import('@fai/friendli-suite/pages/auth/signup/check-inbox')
);
const AuthForgotPasswordPage = React.lazy(
  () => import('@fai/friendli-suite/pages/auth/forgot-password')
);
const AuthResetPasswordPage = React.lazy(
  () => import('@fai/friendli-suite/pages/auth/reset-password')
);
const AuthOnboardingFormContextWrapper = React.lazy(
  () => import('@fai/friendli-suite/views/auth/onboarding/form-context-wrapper')
);
const AuthOnboardingGeneralProfilePage = React.lazy(
  () => import('@fai/friendli-suite/pages/auth/onboarding/general/profile')
);
const AuthOnboardingGeneralProductSelectPage = React.lazy(
  () => import('@fai/friendli-suite/pages/auth/onboarding/general/product-select')
);
const AuthOnboardingGeneralDedicatedEndpointsChoosePlanPage = React.lazy(
  () => import('@fai/friendli-suite/pages/auth/onboarding/general/dedicated-endpoints/choose-plan')
);
const AuthOnboardingProductDedicatedEndpointsProfilePage = React.lazy(
  () =>
    import('@fai/friendli-suite/pages/auth/onboarding/product-scoped/dedicated-endpoints/profile')
);
const AuthOnboardingProductDedicatedEndpointsChoosePlanPage = React.lazy(
  () =>
    import(
      '@fai/friendli-suite/pages/auth/onboarding/product-scoped/dedicated-endpoints/choose-plan'
    )
);
const AuthOnboardingProductContainerProfilePage = React.lazy(
  () => import('@fai/friendli-suite/pages/auth/onboarding/product-scoped/container/profile')
);
const AuthOnboardingProductServerlessEndpointsProfilePage = React.lazy(
  () =>
    import('@fai/friendli-suite/pages/auth/onboarding/product-scoped/serverless-endpoints/profile')
);

const authBeforeLoginRoutes: RouteObject[] = [
  {
    path: 'login',
    Component: () => (
      <AuthLayout
        rightItems={[docsHeaderLinkProps, pricingHeaderLinkProps, websiteHeaderLinkProps]}
      />
    ),
    children: [{ index: true, Component: AuthLoginPage }],
  },
  {
    path: 'signup',
    Component: () => (
      <AuthLayout
        rightItems={[docsHeaderLinkProps, pricingHeaderLinkProps, websiteHeaderLinkProps]}
      />
    ),
    children: [
      { path: 'self-register', Component: AuthSignupSelfRegisterPage },
      { path: 'check-inbox', Component: SignupCheckInboxPage },
    ],
  },
  {
    path: 'forgot-password',
    Component: () => <AuthLayout rightItems={[loginHeaderLinkProps]} />,
    children: [{ index: true, Component: AuthForgotPasswordPage }],
  },
  {
    path: 'reset-password',
    Component: AuthLayout,
    children: [{ index: true, Component: AuthResetPasswordPage }],
  },
];

const forceSignOutRoutes: RouteObject[] = [
  {
    path: 'login/cli',
    Component: () => (
      <AuthLayout
        rightItems={[docsHeaderLinkProps, pricingHeaderLinkProps, websiteHeaderLinkProps]}
      />
    ),
    children: [{ index: true, Component: AuthCliLoginPage }],
  },
];

const onboardingGeneralRoutes: RouteObject[] = [
  {
    path: 'profile',
    Component: AuthOnboardingGeneralProfilePage,
  },
  {
    path: 'product-select',
    Component: AuthOnboardingGeneralProductSelectPage,
  },
  // After user selects product from general onboarding process
  {
    path: 'dedicated-endpoints',
    children: [
      {
        path: 'choose-plan',
        Component: AuthOnboardingGeneralDedicatedEndpointsChoosePlanPage,
      },
    ],
  },
  {
    index: true,
    loader: () => redirect('profile'),
  },
  {
    path: '*',
    loader: () => redirect('profile'),
  },
];

const onboardingDedicatedEndpointsRoutes: RouteObject[] = [
  {
    path: 'profile',
    Component: AuthOnboardingProductDedicatedEndpointsProfilePage,
  },
  {
    path: 'choose-plan',
    Component: AuthOnboardingProductDedicatedEndpointsChoosePlanPage,
  },
  {
    index: true,
    loader: () => redirect('profile'),
  },
  {
    path: '*',
    loader: () => redirect('profile'),
  },
];

const onboardingContainerRoutes: RouteObject[] = [
  {
    path: 'profile',
    Component: AuthOnboardingProductContainerProfilePage,
  },

  {
    index: true,
    loader: () => redirect('profile'),
  },
  {
    path: '*',
    loader: () => redirect('profile'),
  },
];

const onboardingServerlessEndpointsRoutes: RouteObject[] = [
  {
    path: 'profile',
    Component: AuthOnboardingProductServerlessEndpointsProfilePage,
  },
  {
    index: true,
    loader: () => redirect('profile'),
  },
  {
    path: '*',
    loader: () => redirect('profile'),
  },
];

const authAfterLoginRoutes: RouteObject[] = [
  {
    path: 'onboarding',
    Component: AuthLayout,
    loader: pipeLoaders({
      sequence: [redirectIfSessionExpired, onBoardingRedirection('/onboarding/invite')],
    }),
    children: [
      {
        path: 'general',
        Component: AuthOnboardingFormContextWrapper,
        children: onboardingGeneralRoutes,
      },
      // product scoped
      {
        path: 'dedicated-endpoints',
        Component: AuthOnboardingFormContextWrapper,
        children: onboardingDedicatedEndpointsRoutes,
      },
      {
        path: 'container',
        Component: AuthOnboardingFormContextWrapper,
        children: onboardingContainerRoutes,
      },
      {
        path: 'serverless-endpoints',
        Component: AuthOnboardingFormContextWrapper,
        children: onboardingServerlessEndpointsRoutes,
      },
      {
        index: true,
        loader: () => redirect('general'),
      },
      {
        path: '*',
        loader: () => redirect('general'),
      },
    ],
  },
];

export { authBeforeLoginRoutes, forceSignOutRoutes, authAfterLoginRoutes };
